"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { fetchMfaStatus, MFA_CHANGED_EVENT, type MfaStatus } from "@/lib/api/mfa";
import { cUserAccountsTranslations } from "./CUserAccounts.i18n";
import styles from "./MfaGate.module.css";

/**
 * Bramka MFA dla shellu konta (c_user_accounts) - analog `ProcedureGuard`
 * bannera dla procedur w terminie (miękkie info + link, bez blokady).
 *
 * **Zachowanie:**
 *   - `mfa_option === "disabled"` / `"optional"` -> przezroczyste (children)
 *   - `mfa_option === "required"` + `enabled === true` -> przezroczyste (user
 *     już skonfigurował, wszystko OK)
 *   - `mfa_option === "required"` + `enabled === false` -> banner nad content'em
 *     z linkiem do zakładki "Bezpieczeństwo" (`#security` hash routing w
 *     ProfileTabs). User zachowuje pełny dostęp do konta - banner jest
 *     miękkim przypomnieniem, nie blokadą.
 *
 * **Fail-soft:** błąd sieci zwraca `option: "disabled"` (fetchMfaStatus) →
 * bannera nie ma. Podczas ładowania renderuje children bez bannera.
 */
export function MfaGuard({
  children,
}: {
  children: ReactNode;
}) {
  const t = useT(cUserAccountsTranslations);
  const [status, setStatus] = useState<MfaStatus | null>(null);

  const refresh = useCallback(async () => {
    const s = await fetchMfaStatus();
    setStatus(s);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  //Nasłuch custom event dispatch'owanego przez ProfileTabSecurity po każdym
  //enable/disable. Bez tego banner "wymagane MFA" wisiał do refresh strony
  //nawet po tym jak user zapisał konfigurację w zakładce Bezpieczeństwo
  //(hashchange nie odpala się gdy user zostaje na tym samym `#security`).
  useEffect(() => {
    const onMfaChanged = () => void refresh();
    window.addEventListener(MFA_CHANGED_EVENT, onMfaChanged);
    return () => window.removeEventListener(MFA_CHANGED_EVENT, onMfaChanged);
  }, [refresh]);

  const shouldShowBanner =
    status !== null && status.option === "required" && status.enabled === false;

  return (
    <>
      {shouldShowBanner && (
        <div className={styles.banner} role="status">
          <Icon name="warning" className={styles.bannerIcon} />
          <div className={styles.bannerBody}>
            <span className={styles.bannerTitle}>{t.mfaGuard.title}</span>
            <p className={styles.bannerText}>{t.mfaGuard.description}</p>
            <a className={styles.bannerLink} href="#security">
              <Icon name="lock" />
              {t.mfaGuard.linkToSecurity}
            </a>
          </div>
        </div>
      )}
      {children}
    </>
  );
}
